import axios from "axios";
import API_BASE_URL from "./config";
import { isTokenValid, clearAuthStorage, isSessionActive, updateLastActivity, shouldRefreshTokenSoon } from "./utils/tokenUtils";

const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" }
});

let isRefreshingLocal = false;

const forceLogout = () => {
  clearAuthStorage();
  window.location.href = "/login";
};

const tryRefreshToken = async () => {
  if (isRefreshingLocal) return;
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return;
  isRefreshingLocal = true;
  try {
    const { data } = await axios.post(`${API_BASE_URL}/User/refresh-token`, { refreshToken });
    if (data?.token && data?.refreshToken) {
      localStorage.setItem("token", data.token);
      localStorage.setItem("refreshToken", data.refreshToken);
    }
  } catch {
  } finally {
    isRefreshingLocal = false;
  }
};

instance.interceptors.request.use(cfg => {
  const t = localStorage.getItem("token");
  if (t) {
    if (!cfg._skipAuthCheck) {
      if (!isSessionActive()) {
        forceLogout();
        return Promise.reject(new Error("Session expired due to inactivity"));
      }
      if (!isTokenValid(t)) {
        forceLogout();
        return Promise.reject(new Error("Token expired"));
      }
      if (shouldRefreshTokenSoon(t) && !isRefreshingLocal) {
        tryRefreshToken();
      }
    }
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  if (!cfg._skipAuthCheck) {
    updateLastActivity();
  }
  return cfg;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

instance.interceptors.response.use(
  (response) => {
    if (!response.config._skipAuthCheck) {
      updateLastActivity();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (
        originalRequest.url?.includes("/User/login") ||
        originalRequest.url?.includes("/User/refresh-token")
      ) {
        if (originalRequest.url?.includes("/User/refresh-token")) {
          forceLogout();
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        isRefreshing = false;
        forceLogout();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/User/refresh-token`,
          { refreshToken }
        );

        localStorage.setItem("token", data.token);
        localStorage.setItem("refreshToken", data.refreshToken);
        processQueue(null, data.token);

        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return instance(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403) {
      const t = localStorage.getItem("token");
      if (!t || !isTokenValid(t) || !isSessionActive()) {
        forceLogout();
      }
    }

    return Promise.reject(error);
  }
);

export default instance;